/**
 * scenarioLayerGeometry — the ONE Generation-scenario → SiteLayer converter.
 *
 * Extracted verbatim from the server's `buildGenerationImpactScenarioLayers`
 * (generationImpactServer.ts) so the client can render a DRAFT preview of the
 * promoted-but-unpublished layout on the Site Map tab through the exact same
 * kind→category mapping, attributes, exclusion zones, receptors, and noise
 * contours the publish path freezes. The server function is now a thin
 * wrapper: it supplies `importedAt = now`, keeps the default `genimpact-`
 * id prefix, and re-throws {@link GenerationScenarioGeometryError} as its
 * UserFunctionError — publish output stays byte-identical (locked by the
 * generationImpactServer test suite).
 *
 * Solver integrity note: NOTHING here touches solver math — the test-fit is
 * produced by the shared `buildGenerationParcelTestFit` exactly as before;
 * this module only maps its outputs to map layers.
 */

import type { SiteLayer } from '../../types/site';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import {
  buildGenerationParcelTestFit,
  type GenerationParcelTestFitResult,
} from './generationLayoutGeometry';
import { solveGenerationNoise, toGenerationNoiseScenario } from './solveGenerationImpact';
import { firestoreNestedArrayDecodeDefect } from './firestoreNestedArrayCodec';

const EARTH_M_PER_DEG_LAT = 111_320;

/** Geometry preconditions the caller must surface (the server maps this to
 *  UserFunctionError('failed-precondition', …)). */
export class GenerationScenarioGeometryError extends Error {
  readonly code = 'failed-precondition' as const;
  constructor(message: string) {
    super(message);
    this.name = 'GenerationScenarioGeometryError';
  }
}

export interface BuildGenerationScenarioLayersOptions {
  /** Stamp for `SiteLayer.importedAt` (server passes `now` at publish). */
  importedAt: string;
  /** Layer-id prefix — 'genimpact' (publish, default) or 'draft-gen' (map preview). */
  idPrefix?: string;
  /** `SiteLayer.visible` default; draft previews render translucent but visible. */
  visible?: boolean;
}

export interface GenerationScenarioLayerGeometry {
  layers: SiteLayer[];
  /** The parcel test-fit backing the component polygons (status, containment). */
  testFit: GenerationParcelTestFitResult;
}

export function buildGenerationScenarioLayerGeometry(
  scenario: GenerationImpactScenarioSnapshot,
  parcels: readonly SiteBoundaryParcel[] = [],
  opts: BuildGenerationScenarioLayersOptions,
): GenerationScenarioLayerGeometry {
  // Exclusion-zone polygons and noise rings are the exact shapes the Firestore
  // persistence encoding wraps, and a wrapped ring drawn as geometry is
  // silently WRONG rather than absent. The decode belongs at the Firestore read
  // boundary; this only names a missing one for a developer, and is inert in a
  // deployed process — every SiteMapTab geometry rebuild runs through here.
  const decodeDefect = firestoreNestedArrayDecodeDefect(
    'Scenario geometry',
    scenario.input,
    scenario.result,
  );
  if (decodeDefect) throw new GenerationScenarioGeometryError(decodeDefect);
  const idPrefix = opts.idPrefix ?? 'genimpact';
  const anchor = scenario.input.layoutAnchor;
  if (!anchor) throw new GenerationScenarioGeometryError('Set a layout anchor before publishing geometry');
  const common = {
    source: 'scenario-derived' as const,
    importedAt: opts.importedAt,
    visible: opts.visible ?? true,
    attributes: { scenarioId: scenario.id, modelVersion: scenario.result.modelVersion },
  };
  const testFit = buildGenerationParcelTestFit({
    parcels,
    layout: scenario.input.layout,
    fallbackAnchor: anchor,
    generationBlockCount: Math.max(
      1,
      scenario.input.units.reduce((sum, unit) => sum + unit.count, 0),
    ),
    footprint: scenario.result.footprint,
    installedReservations: scenario.input.projectDutyBasis?.installedReservations,
  });
  if (scenario.input.layout?.targetParcelId && !testFit.parcel) {
    throw new GenerationScenarioGeometryError(
      testFit.error ?? 'The selected layout parcel no longer exists',
    );
  }
  if (!testFit.plan || testFit.error) {
    throw new GenerationScenarioGeometryError(
      testFit.error ?? 'Layout inputs do not produce publishable geometry',
    );
  }
  const layers: SiteLayer[] = testFit.components.map((component): SiteLayer => {
    const rectangle = component.rectangle;
    const category = rectangle.kind === 'switchyard'
      ? 'infrastructure.electrical' as const
      : rectangle.kind === 'natural-gas-route' || rectangle.kind === 'co2-export-route'
        ? 'infrastructure.pipeline' as const
        : rectangle.kind === 'construction-laydown'
          ? 'concept.expansion' as const
      : rectangle.kind === 'ccs-capture' || rectangle.kind === 'ccs-compression'
        ? 'concept.ccs' as const
        // Studio kinds (schemaVersion 2): circulation → transport; planned
        // structures → expansion; generators → generation (also the unknown
        // fallback, preserved for forward compatibility).
        : rectangle.kind === 'access-road' || rectangle.kind === 'road' || rectangle.kind === 'parking'
          ? 'infrastructure.transport' as const
          : rectangle.kind === 'building' || rectangle.kind === 'data-center' || rectangle.kind === 'custom'
            ? 'concept.expansion' as const
            : 'concept.generation' as const;
    return {
      ...common,
      id: `${idPrefix}-${scenario.id}-${rectangle.id}`,
      name: `${scenario.name} — ${rectangle.label}`,
      category,
      geometry: { type: 'polygon', coordinates: component.coordinates },
      description: 'Deterministic parcel-containment screening block; not construction CAD or a constructability opinion.',
      attributes: {
        ...common.attributes,
        layoutPhysicalComponent: true,
        layoutComponentKind: rectangle.kind,
        layoutAreaAcres: rectangle.areaAcres,
        layoutWidthM: rectangle.widthM,
        layoutHeightM: rectangle.heightM,
        layoutRotationDeg: testFit.layout.rotationDeg,
        layoutFitStatus: testFit.status,
        layoutContainmentPct: testFit.containmentPct,
        layoutOutsideAcres: component.outsideAcres,
        layoutParcelEdgeClearanceM: component.parcelEdgeClearanceM,
        layoutParcelEdgeSetbackM: testFit.layout.edgeSetbackM,
        layoutParcelEdgeSetbackSatisfied: component.meetsParcelEdgeSetback,
        layoutDimensionBasis: rectangle.dimensionBasis,
        layoutLifecycle: rectangle.lifecycle,
        ...(testFit.parcel ? { layoutParcelId: testFit.parcel.id } : {}),
        ...(component.exclusionOverlapAcres !== undefined
          ? { layoutExclusionOverlapAcres: component.exclusionOverlapAcres }
          : {}),
        ...(testFit.usable
          ? {
              layoutUsableAcres: testFit.usable.usableAcres,
              layoutExcludedAcres: testFit.usable.excludedAcres,
              layoutFemaStatus: testFit.usable.femaStatus,
            }
          : {}),
      },
    };
  });
  for (const zone of testFit.layout.exclusions?.zones ?? []) {
    zone.polygons.forEach((ring, ringIndex) => {
      if (ring.length < 3) return;
      layers.push({
        ...common,
        id: `${idPrefix}-${scenario.id}-exclusion-${zone.id}${zone.polygons.length > 1 ? `-${ringIndex + 1}` : ''}`,
        name: `${scenario.name} — ${zone.label}`,
        category: 'constraint.environmental',
        geometry: { type: 'polygon', coordinates: ring.map(point => ({ ...point })) },
        description: 'No-build exclusion zone subtracted from the buildable area in the published layout; screening-grade source geometry, not a regulatory determination.',
        attributes: {
          ...common.attributes,
          layoutExclusion: true,
          layoutExclusionSource: zone.source,
          layoutExclusionKind: zone.kind,
          ...(zone.provenance
            ? {
                layoutExclusionProviderId: zone.provenance.providerId,
                layoutExclusionRetrievedAt: zone.provenance.retrievedAt,
              }
            : {}),
        },
      });
    });
  }
  for (const receptor of scenario.input.receptors) {
    if (!receptor.location) continue;
    layers.push({
      ...common, id: `${idPrefix}-${scenario.id}-receptor-${receptor.id}`, name: receptor.label, category: 'impact.receptor',
      geometry: { type: 'point', coordinates: [receptor.location] },
      description: 'Noise/light screening receptor.',
    });
  }
  const noiseField = scenario.result.noiseField;
  if (noiseField) {
    // grid-screening-v2 — one polygon layer per band ring, ascending level
    // (later/higher bands draw on top via the overlay z-order). Geometry
    // comes from the FROZEN result, so draft preview and publish emit
    // byte-identical bands.
    noiseField.bands.forEach((band, bandIndex) => {
      band.rings.forEach((ring, ringIndex) => {
        layers.push({
          ...common,
          id: `${idPrefix}-${scenario.id}-noiseband-${band.levelDbA}${band.rings.length > 1 ? `-${ringIndex + 1}` : ''}`,
          name: `${scenario.name} — ≥${band.levelDbA} dBA noise band`,
          category: 'impact.noise',
          geometry: { type: 'polygon', coordinates: ring.map(point => ({ ...point })) },
          description: 'Grid-screening propagation band (barriers + atmospheric + ground terms); screening-grade single-band model, not ISO 9613-2 compliance.',
          attributes: {
            ...common.attributes,
            noiseFieldLevelDbA: band.levelDbA,
            noiseFieldModelVersion: noiseField.modelVersion,
            noiseBandRank: bandIndex + 1,
          },
        });
      });
    });
  } else {
    for (const contour of scenarioNoiseContours(scenario)) {
      if (contour.status !== 'calculated' || contour.radiusM == null) continue;
      const { levelDbA, radiusM } = contour;
      layers.push({
        ...common,
        id: `${idPrefix}-${scenario.id}-noise-${levelDbA}`,
        name: `${scenario.name} — ${levelDbA} dBA screening contour`,
        category: 'impact.noise',
        geometry: { type: 'polygon', coordinates: circle(testFit.anchor, radiusM) },
        description: `Frozen deterministic free-field ${levelDbA} dBA screening contour; not ISO 9613 compliance.`,
        attributes: {
          ...common.attributes,
          noiseContourLevelDbA: levelDbA,
          noiseContourRadiusM: radiusM,
          noiseContourStatus: contour.status,
          noiseModelBasis: scenario.result.noiseModelBasis ?? 'free-field-screening',
        },
      });
    }
  }
  // User-drawn acoustic screens publish alongside the field they shaped.
  for (const barrier of scenario.input.layout?.acousticBarriers ?? []) {
    layers.push({
      ...common,
      id: `${idPrefix}-${scenario.id}-barrier-${barrier.id}`,
      name: `${scenario.name} — ${barrier.label}`,
      category: 'impact.barrier',
      geometry: {
        type: barrier.kind === 'building' ? 'polygon' : 'line',
        coordinates: barrier.kind === 'building'
          ? [...barrier.path.map(point => ({ ...point })), { ...barrier.path[0] }]
          : barrier.path.map(point => ({ ...point })),
      },
      description: `${barrier.kind === 'berm' ? 'Earth berm' : barrier.kind === 'building' ? 'Building outline' : 'Wall'} acoustic screen, ${barrier.heightM} m high (Maekawa thin-screen model).`,
      attributes: {
        ...common.attributes,
        barrierKind: barrier.kind,
        barrierHeightM: barrier.heightM,
      },
    });
  }
  return { layers, testFit };
}

/** 64-segment closed ring approximating a radius around the anchor. */
export function circle(anchor: { lat: number; lng: number }, radiusM: number): Array<{ lat: number; lng: number }> {
  const metersPerDegLng = EARTH_M_PER_DEG_LAT * Math.cos(anchor.lat * Math.PI / 180);
  return Array.from({ length: 65 }, (_, index) => {
    const angle = (index / 64) * Math.PI * 2;
    return {
      lat: anchor.lat + Math.cos(angle) * radiusM / EARTH_M_PER_DEG_LAT,
      lng: anchor.lng + Math.sin(angle) * radiusM / metersPerDegLng,
    };
  });
}

export function scenarioNoiseContours(scenario: GenerationImpactScenarioSnapshot) {
  // New snapshots freeze the canonical adapter output in the workbench result.
  // Preserve that exact projection for reproducible maps. Older v1 snapshots
  // predate noiseContours, so use the same adapter/kernel as report generation
  // instead of reviving the removed sound-power-only approximation.
  return scenario.result.noiseContours
    ?? solveGenerationNoise(toGenerationNoiseScenario(scenario.input)).contours;
}
